import React, { createContext, useState } from "react";
import { useCallback } from "react";
import { transformCamelCaseToSnakeCase } from "../../utils/stringUtils";
import { pageAllBankStatementTransactionsFarm } from "../../services/bankStatementService";
import { useSelector } from "react-redux";
import moment from "moment";

const STATEMENT_CONTEXT_STATE = {
  dataList: [],
  isLoadingDataList: false,
  tableProperties: {
    size: 10,
    page: 0,
    tableFilters: {},
    tableSorters: {},
    search: null,
  },
  fetchData: (page = 0, size = 10, sort = {}, search = null) => {},
  handleTableChange: (pagination, filters, sorter) => {},
  // Define any initial state or functions you need here
  showStatementImportForm: false,
  handleCloseStatementImportForm: () => {},
  handleOpenStatementImportForm: () => {},
};

export const StatementContext = createContext(STATEMENT_CONTEXT_STATE);

export const StatementProvider = ({ children }) => {
  const [showStatementImportForm, setShowStatementImportForm] = useState(
    STATEMENT_CONTEXT_STATE.showStatementImportForm
  );
  const [dataList, setDataList] = useState([]);
  const [isLoadingDataList, setIsLoadingDataList] = useState(false);
  const [tableProperties, setTableProperties] = useState({
    size: 10,
    page: 0,
    tableFilters: {},
    tableSorters: {},
    search: null,
  });
  const {
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const getSortString = useCallback((sorter) => {
    let customSort = sorter;

    if (sorter && Object.entries(sorter).length > 0) {
      const { field } = sorter;

      const fieldS = field ? transformCamelCaseToSnakeCase(field) : null;

      customSort = {
        ...customSort,
        field: fieldS,
      };
    }

    return customSort;
  }, []);

  const getSearchString = useCallback((filters) => {
    let search = "";

    if (filters && Object.entries(filters).length > 0) {
      Object.entries(filters).forEach(([field, value]) => {
        if (value && value.length > 0) {
          const fieldS = field ? transformCamelCaseToSnakeCase(field) : null;
          if (fieldS === "amount") {
            const operatorS =
              value[1] === "equal"
                ? "="
                : value[1] === "greaterThan"
                ? ">"
                : value[1] === "greaterOrEqualThan"
                ? ">="
                : value[1] === "lessThan"
                ? "<"
                : value[1] === "lessOrEqualThan"
                ? "<="
                : "=";
            search =
              search === ""
                ? `${fieldS} ${operatorS} '${value[0]}'`
                : `${search};${fieldS} ${operatorS} '${value[0]}'`;
          } else if (fieldS === "transaction_date") {
            const operatorS =
              value[1] === "equal"
                ? "="
                : value[1] === "greaterThan"
                ? ">"
                : value[1] === "greaterOrEqualThan"
                ? ">="
                : value[1] === "lessThan"
                ? "<"
                : value[1] === "lessOrEqualThan"
                ? "<="
                : "=";

            search =
              search === ""
                ? `cast(${fieldS} as date) ${operatorS} cast('${moment(
                    value[0]
                  ).format("YYYY-MM-DD")}' as date)`
                : `${search};cast(${fieldS} as date) ${operatorS} cast('${moment(
                    value[0]
                  ).format("YYYY-MM-DD")}' as date)`;
          } else if (fieldS === "type") {
            search =
              search === ""
                ? `${fieldS} in (${value
                    .map((o) => (o = `'${o}'`))
                    .join(", ")})`
                : `${search};${fieldS} in (${value
                    .map((o) => (o = `'${o}'`))
                    .join(", ")})`;
          } else if (fieldS === "name" || fieldS === "memo") {
            search =
              search === ""
                ? `(upper(name) like upper('%25${value}%25') OR upper(memo) like upper('%25${value}%25'))`
                : `${search};(upper(name) like upper('%25${value}%25') OR upper(memo) like upper('%25${value}%25'))`;
          } else if (fieldS === "conciliated") {
            search =
              search === ""
                ? `should_conciliate = true;${fieldS} = ${value}`
                : `${search};should_conciliate = true;${fieldS} = ${value}`;
          } else {
            search =
              search === ""
                ? `upper(${fieldS}) like upper('%25${value}%25')`
                : `${search};upper(${fieldS}) like upper('%25${value}%25')`;
          }
        }
      });
    }

    return search !== "" ? search : "";
  }, []);

  const fetchData = useCallback(
    async (page = 0, size = 10, sort = {}, search = null) => {
      setIsLoadingDataList(true);
      try {
        const {
          data: { results },
        } = await pageAllBankStatementTransactionsFarm({
          groupId,
          farmId,
          page,
          size,
          sort,
          search,
        });
        setDataList(results);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoadingDataList(false);
      }
    },
    [farmId, groupId]
  );

  const handleCloseStatementImportForm = () => {
    setShowStatementImportForm(false);
  };

  const handleOpenStatementImportForm = () => {
    setShowStatementImportForm(true);
  };

  const handleTableChange = useCallback(
    async (pagination = null, filters = null, sorter = null) => {
      if (pagination && filters && sorter) {
        let customSort = getSortString(sorter);
        let search = getSearchString(filters);

        setTableProperties({
          tableFilters: filters,
          tableSorters: sorter,
          size: pagination.pageSize,
          page: pagination.current,
          search,
        });

        await fetchData(
          pagination.current,
          pagination.pageSize,
          customSort,
          search
        );
      } else {
        setTableProperties({
          size: 10,
          page: 0,
          tableFilters: {},
          tableSorters: {},
          search: null,
        });
        await fetchData(0, 10, {}, "");
      }
    },
    [fetchData, getSearchString, getSortString]
  );

  const values = {
    dataList,
    isLoadingDataList,
    tableProperties,
    fetchData,
    handleTableChange,
    showStatementImportForm,
    handleCloseStatementImportForm,
    handleOpenStatementImportForm,
  };

  return (
    <StatementContext.Provider value={values}>
      {children}
    </StatementContext.Provider>
  );
};
